import React, { useState, useReducer, useEffect, Suspense, lazy, useContext } from "react";

import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, UNSAFE_DataRouterStateContext, useLocation, Outlet, useNavigate, Navigate } from "react-router-dom";

import { pathToRegexp } from "path-to-regexp";

import { useImmerReducer } from "use-immer";

import { updateTargetLanguageCookie } from "./data/languageUtils";

import StateContext from "./StateContext";
import DispatchContext from "./DispatchContext";
import LoadingDotsIcon from "./components/LoadingDotsIcon";

// Lazy-loaded components
const Header = lazy(() => import("./components/Header"));
const AuthOverlay = lazy(() => import("./components/AuthOverlay"));
const HomeGuest = lazy(() => import("./components/HomeGuest"));
const Footer = lazy(() => import("./components/Footer"));
const About = lazy(() => import("./components/About"));
const MobileMessage = lazy(() => import("./components/MobileMessage"));
const Terms = lazy(() => import("./components/Terms"));
const Home = lazy(() => import("./components/Home"));
const CreatePost = lazy(() => import("./components/CreatePost"));
const ViewSinglePost = lazy(() => import("./components/ViewSinglePost"));
const FlashMessages = lazy(() => import("./components/FlashMessages"));
const Profile = lazy(() => import("./components/Profile"));
const EditPost = lazy(() => import("./components/EditPost"));
const NotFound = lazy(() => import("./components/NotFound"));
const EditResponse = lazy(() => import("./components/EditResponse"));
const Search = lazy(() => import("./components/Search"));
const StartMixer = lazy(() => import("./components/StartMixer"));
const FillSurveySingle = lazy(() => import("./components/FillSurveySingle"));
const ThankYou = lazy(() => import("./components/ThankYou"));
const MySideNav = lazy(() => import("./components/MySideNav"));
const GEMSideNav = lazy(() => import("./components/GEMSideNav"));
const SelectModel = lazy(() => import("./components/SelectModel"));
const ModelDetails = lazy(() => import("./components/ModelDetails"));
const ModelSetup = lazy(() => import("./components/ModelSetup"));
const ModelDash = lazy(() => import("./components/ModelDash"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const ModelSettings = lazy(() => import("./components/ModelSettings"));
const ModelResponses = lazy(() => import("./components/ModelResponses"));
const ModelDetailCreator = lazy(() => import("./components/ModelDetailCreator"));
const GEMAnalysis = lazy(() => import("./components/report001/GEM-Analysis"));
const SERVPERFAnalysis = lazy(() => import("./components/report001/SERVPERF-Analysis"));
const GlobalSettings = lazy(() => import("./components/GlobalSettings"));
const AccessKnowledgeBase = lazy(() => import("./components/AccessKnowledgeBase"));

const GEMAnalysis1 = lazy(() => import("./components/report001/GEM-Analysis_One"));
const HeaderMobile = lazy(() => import("./components/HeaderMobile"));
const Landing = lazy(() => import("./components/Landing"));
const FooterLanding = lazy(() => import("./components/FooterLanding"));
const LandingGEM = lazy(() => import("./components/LandingGEM"));
const VerifyEmail = lazy(() => import("./components/VerifyEmail"));
const RequestResetPassword = lazy(() => import("./components/RequestResetPassword"));
const ResetPassword = lazy(() => import("./components/ResetPassword"));

const NewSurvey = lazy(() => import("./components/NewSurvey"));
const GoogleForms = lazy(() => import("./components/GoogleForms"));
const NewDash = lazy(() => import("./components/NewDash"));
const NewResponses = lazy(() => import("./components/NewResponses"));
const NewDashboard = lazy(() => import("./components/NewDashboard"));
const Customize = lazy(() => import("./components/Customize"));
const SurveyPlayground = lazy(() => import("./components/SurveyPlayground"));

const ViewFeedbacks = lazy(() => import("./components/ViewFeedbacks"));
const ViewFeedbacksOpenAI = lazy(() => import("./components/ViewFeedbacksOpenAI"));
// const NewTestSurvey = lazy(() => import("./components/NewTestSurvey"));
// const MinimalSurveyComponent = lazy(() => import("./components/MinimalSurveyComponent"));
const OpenVisuals = lazy(() => import("./components/OpenVisuals"));
const StartPoint = lazy(() => import("./components/StartPoint"));
const MergedSurveyForm = lazy(() => import("./components/MergedSurveyForm"));

import { CSSTransition } from "react-transition-group";

import axios from "axios";
const EditSurvey = lazy(() => import("./components/EditSurvey"));
const FormPlayground = lazy(() => import("./components/FormPlayground"));
const ResponsePlayground = lazy(() => import("./components/ResponsePlayground"));
const TablePlayground = lazy(() => import("./components/TablePlayground"));
const SurveyAnalysisCard = lazy(() => import("./components/SurveyAnalysisCard"));
const FormDemo = lazy(() => import("./components/FormDemo"));
const URLInputPage = lazy(() => import("./components/URLInputPage"));
const DemoFormPage = lazy(() => import("./components/DemoFormPage"));
const SelectProduct = lazy(() => import("./components/SelectProduct"));
const EditMagicSurvey = lazy(() => import("./components/EditMagicSurvey"));
const URLInputPageLoggedIn = lazy(() => import("./components/URLInputPageLoggedIn"));
const MagicFormPage = lazy(() => import("./components/MagicFormPage"));
const HomeSelect = lazy(() => import("./components/HomeSelect"));
const SurveySender = lazy(() => import("./components/SurveySender"));

const LoginPage = lazy(() => import("./components/LoginPage"));
const LanguageSelector = lazy(() => import("./components/LanguageSelector"));
const MediaManager = lazy(() => import("./components/MediaManager"));
const SelectOption = lazy(() => import("./components/SelectOption"));
const ExternalPlatforms = lazy(() => import("./components/ExternalPlatforms"));
const TallyIntegration = lazy(() => import("./components/TallyIntegration"));
const CreateHookSurvey = lazy(() => import("./components/CreateHookSurvey"));
const SelectSwOption = lazy(() => import("./components/SelectSwOption"));
const AuthCallback = lazy(() => import("./components/AuthCallback"));
const SelectAnalysisOption = lazy(() => import("./components/SelectAnalysisOption"));
const DocumentUpload = lazy(() => import("./components/DocumentUpload"));
const ExcelUpload = lazy(() => import("./components/ExcelUpload"));
const PublicDash = lazy(() => import("./components/PublicDash"));

const TestSurvey = lazy(() => import("./components/TestSurvey"));
const PricingPlans = lazy(() => import("./components/PricingPlans"));
const PersonaLibrary = lazy(() => import("./components/PersonaLibrary"));
const InterviewLibrary = lazy(() => import("./components/InterviewLibrary"));

import GradioInterface from "./components/GradioInterface";
import "./main.css";
import URLInputPageBeta from "./components/URLInputPageBeta";
import axiosInstance from "./utils/axiosConfig";

// Set Axios base URL based on environment
const backendUrl = process.env.NODE_ENV === "production" ? process.env.REACT_APP_BACKEND_URL_PROD : process.env.REACT_APP_BACKEND_URL_DEV;

axios.defaults.baseURL = backendUrl;
axiosInstance.defaults.baseURL = backendUrl;

console.log("default base", axios.defaults.baseURL);
function Main() {
  const mode = "development";
  mode === "production" && (console.log = () => {});

  const initialState = {
    loggedIn: Boolean(sessionStorage.getItem("complexappToken")),
    flashMessages: [],
    user: {
      token: sessionStorage.getItem("complexappToken"),
      username: sessionStorage.getItem("complexappUsername"),
      pythanksClientId: sessionStorage.getItem("pythanksClientId"),
      email: sessionStorage.getItem("email"),
      emailValidated: sessionStorage.getItem("emailValidated"),
      plan: sessionStorage.getItem("plan"),
      language: sessionStorage.getItem("currentLanguage" || "English"),
    },
    surveys: [],
    isSearchOpen: false,
    showHeader: true,
    activeSurveyId: null,
    activeMenuItem: "analysis",

  };

  const ourReducer = (draft, action) => {
    switch (action.type) {
      case "login":
        draft.loggedIn = true;
        draft.user = action.data;
        console.log("action.data", action.data);

        console.log("user", draft.user);
        return;
      case "logout":
        draft.loggedIn = false;
        return;
      case "flashMessage":
        // draft.flashMessages.push(action.value);
        return;
      case "openSearch":
        draft.isSearchOpen = true;
        return;
      case "closeSearch":
        draft.isSearchOpen = false;
        return;
      case "setActiveSurveyId":
        draft.activeSurveyId = action.value;
        return;
      case "setActiveMenuItem":
        draft.activeMenuItem = action.value;
        return;
      case "setNewClient":
        draft.user.newClient = action.value;
        sessionStorage.setItem("newClient", action.value);
        return;
      case "setSurveys":
        draft.surveys = action.value;
        return;
      case "updateSurveyName":
        const survey = draft.surveys.find((s) => s._id === action.value.id);
        if (survey) {
          survey.settings.name = action.value.name;
        }
        return;
    }
  };

  const [state, dispatch] = useImmerReducer(ourReducer, initialState);

  useEffect(() => {
    const splash = document.getElementById("splash");
    if (splash) {
      splash.style.display = "none";
    }
  }, []);

  useEffect(() => {
    if (state.loggedIn) {
      console.log("see user: ", state.user);
      sessionStorage.setItem("complexappToken", state.user.token);
      sessionStorage.setItem("complexappUsername", state.user.username);
      sessionStorage.setItem("complexappAvatar", state.user.avatar);
      sessionStorage.setItem("pythanksClientId", state.user.pythanksClientId);
      sessionStorage.setItem("email", state.user.email);
      sessionStorage.setItem("newClient", state.user.newClient);
      sessionStorage.setItem("emailValidated", state.user.emailValidated);
      sessionStorage.setItem("plan", state.user.plan);
      sessionStorage.setItem("currentLanguage", state.user.language || "English");
    } else {
      sessionStorage.removeItem("complexappToken");
      sessionStorage.removeItem("complexappUsername");
      sessionStorage.removeItem("complexappAvatar");
      sessionStorage.removeItem("pythanksClientId");
      sessionStorage.removeItem("email");
      sessionStorage.removeItem("newClient");
      sessionStorage.removeItem("emailValidated");
      sessionStorage.removeItem("surveyJson");
      sessionStorage.removeItem("surveyCriteria");
      sessionStorage.removeItem("surveyCompany");
      sessionStorage.removeItem("surveyProblem");
      // localStorage.removeItem("clientPlan");
    }
  }, [state.loggedIn]);

  // Example React component or effect where you might set the language
  useEffect(() => {
    if (state.loggedIn && state.user.language) {
      updateTargetLanguageCookie(state.user.language);
      console.log("language set: ", state.user.language);
    }
  }, [state.loggedIn, state.user.language]);

  useEffect(() => {
    if (state.loggedIn) {
      const ourRequest = axios.CancelToken.source();
      const fetchResults = async () => {
        try {
          // First try to check the current token
          const response = await axiosInstance.post("/checkToken", {}, { 
            cancelToken: ourRequest.token,
            withCredentials: true 
          });
          
          if (!response.data) {
            // If token check fails, try to refresh the token
            try {
              const refreshResponse = await axiosInstance.post("/refresh-token", {}, {
                withCredentials: true
              });
              
              if (refreshResponse.data.token) {
                // Update the access token in session storage
                sessionStorage.setItem("complexappToken", refreshResponse.data.token);
                // Update the token in the state
                dispatch({ 
                  type: "login", 
                  data: {
                    ...state.user,
                    token: refreshResponse.data.token
                  }
                });
                return;
              }
            } catch (refreshError) {
              console.log("Failed to refresh token:", refreshError);
            }
            
            // If both token check and refresh fail, log out
            dispatch({ type: "logout" });
            dispatch({ type: "flashMessage", value: "Your session has expired. Please log in again." });
          }
        } catch (err) {
          console.log("There was a problem or the request was cancelled.");
        }
      };
      fetchResults();
      return () => ourRequest.cancel();
    }
  }, []);

  const isMobile = window.innerWidth <= 767;

  function ProtectedRoute({ children }) {
    const state = useContext(StateContext);
    const { loggedIn } = state;
    const location = useLocation();

    console.log("ProtectedRoute - loggedIn:", loggedIn);
    console.log("ProtectedRoute - location:", location.pathname);

    return loggedIn === true ? (
      children
    ) : (
      <Navigate
        to="/start-point"
        state={{ from: location.pathname }} // ✅ Only pass serializable data
        replace
      />
    );
  }

  const ReportLayout = () => {
    return (
      <>
        <Header />
        <Outlet />
      </>
    );
  };

  const MetaLayout = () => {
    return (
      <>
        <Header />
        <MySideNav />
        <Outlet />
      </>
    );
  };

  const FreeLayoutX = () => {
    const isMobile = window.innerWidth <= 767;

    return (
      <>
        {isMobile ? (
          <MobileMessage /> // Render MobileMessage directly on mobile
        ) : (
          <div style={{ display: "flex", height: "100vh" }}>
            {/* Sidebar */}
            <MySideNav />

            {/* Main Content */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              <Header />
              <Outlet />
            </div>
          </div>
        )}
      </>
    );
  };

  function FreeLayout() {
    const { loggedIn } = useContext(StateContext);
    const [showOverlay, setShowOverlay] = useState(false);
    const location = useLocation(); // Get current URL
    const navigate = useNavigate();

    const isMobile = window.innerWidth <= 767;

    useEffect(() => {
      setShowOverlay(!loggedIn);
    }, [loggedIn]);

    // Function to close the overlay
    // Function to close the overlay
    const closeOverlay = () => {
      setShowOverlay(false);
      if (!loggedIn) {
        navigate("/start-point"); // Redirect only if still not logged in
      }
    };

    return (
      <>
        {isMobile ? (
          <MobileMessage />
        ) : (
          <div style={{ display: "flex", height: "100vh" }}>
            <MySideNav />
            <div style={{ flex: 1, overflowY: "auto" }}>
              <Header />
              {loggedIn && <Outlet />}
            </div>
          </div>
        )}

        {/* Pass the current path to AuthOverlay so it knows where to redirect */}
        <AuthOverlay
          isOpen={showOverlay}
          onClose={closeOverlay}
          redirectPath={location.pathname} // Pass current page URL
        />
      </>
    );
  }

  function FreeLayoutNoSidebar() {
    const { loggedIn } = useContext(StateContext);
    const [showOverlay, setShowOverlay] = useState(false);
    const location = useLocation(); // Get current URL
    const navigate = useNavigate();

    const isMobile = window.innerWidth <= 767;

    useEffect(() => {
      setShowOverlay(!loggedIn);
    }, [loggedIn]);

    // Function to close the overlay
    // Function to close the overlay
    const closeOverlay = () => {
      setShowOverlay(false);
      if (!loggedIn) {
        navigate("/start-point"); // Redirect only if still not logged in
      }
    };

    return (
      <>
        {isMobile ? (
          <MobileMessage />
        ) : (
          <div style={{ display: "flex", height: "100vh" }}>
            {/* <MySideNav /> */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              <Header />
              {loggedIn && <Outlet />}
            </div>
          </div>
        )}

        {/* Pass the current path to AuthOverlay so it knows where to redirect */}
        <AuthOverlay
          isOpen={showOverlay}
          onClose={closeOverlay}
          redirectPath={location.pathname} // Pass current page URL
        />
      </>
    );
  }

  function FreeLayoutNoSidebarNoHeader() {
    const { loggedIn } = useContext(StateContext);
    const [showOverlay, setShowOverlay] = useState(false);
    const location = useLocation(); // Get current URL
    const navigate = useNavigate();

    const isMobile = window.innerWidth <= 767;

    useEffect(() => {
      setShowOverlay(!loggedIn);
    }, [loggedIn]);

    // Function to close the overlay
    // Function to close the overlay
    const closeOverlay = () => {
      setShowOverlay(false);
      if (!loggedIn) {
        navigate("/start-point"); // Redirect only if still not logged in
      }
    };

    return (
      <>
        {isMobile ? (
          <MobileMessage />
        ) : (
          <div style={{ display: "flex", height: "100vh" }}>
            {/* <MySideNav /> */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {/* <Header /> */}
              {loggedIn && <Outlet />}
            </div>
          </div>
        )}

        {/* Pass the current path to AuthOverlay so it knows where to redirect */}
        <AuthOverlay
          isOpen={showOverlay}
          onClose={closeOverlay}
          redirectPath={location.pathname} // Pass current page URL
        />
      </>
    );
  }

  const LandingLayout = () => {
    return (
      <>
        {!isMobile && <Header />}
        {isMobile && <HeaderMobile />}
        <Outlet />
      </>
    );
  };

  const NoLayout = () => {
    const isMobile = window.innerWidth <= 767;

    return (
      <>
        {isMobile ? (
          <MobileMessage /> // Render MobileMessage directly on mobile
        ) : (
          <div style={{ display: "flex", height: "100vh" }}>
            {/* Sidebar */}
            {/* <MySideNav /> */}

            {/* Main Content */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              {/* <Header /> */}
              <Outlet />
            </div>
          </div>
        )}
      </>
    );
  };

  const StartLayout = () => {
    const isMobile = window.innerWidth <= 767;
    return (
      <>
        {isMobile ? (
          <MobileMessage /> // Render MobileMessage directly on mobile
        ) : (
          <div style={{ display: "flex", height: "100vh" }}>
            {/* Sidebar */}
            <MySideNav />

            {/* Main Content */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              <Header />
              <Outlet />
            </div>
          </div>
        )}
      </>
    );
  };
  ``;

  return (
    <StateContext.Provider value={state}>
      <DispatchContext.Provider value={dispatch}>
        <BrowserRouter>
          {/* <Suspense fallback={<div>Loading...</div>}> */}
          <Suspense fallback={<LoadingDotsIcon />}>
            <FlashMessages messages={state.flashMessages} />
            <Routes>
              <Route element={<StartLayout />}>
                <Route path="/start-point" element={<StartPoint />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Home />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route element={<FreeLayout />}>
                <Route path="/model-details/:surveytemplateid" element={<ModelDetails />} />
                <Route path="/model-setup/:surveytemplateid" element={<ModelSetup />} />
                <Route path="/model-responses/:surveyid" element={<NewResponses />} />
                <Route path="/model-dash/:surveyid/:tab?" element={<ModelDash />} />
                <Route path="/model-detail-creator/*" element={<ModelDetailCreator />} />
                <Route path="/global-settings/" element={<GlobalSettings />} />
                <Route path="/customize/:templateid" element={<Customize />} />
                <Route path="/edit-survey/:surveyid" element={<EditSurvey />} />
                <Route path="/view-feedback/:surveyid/:filter?" element={<ViewFeedbacks />} />
                <Route path="/survey-sender/:surveyid" element={<SurveySender />} />
                <Route path="/media-manager" element={<MediaManager />} />
                <Route path="/create-hook-survey" element={<CreateHookSurvey />} />
                <Route path="/select-analysis-option" element={<SelectAnalysisOption />} />
                <Route path="/document-upload" element={<DocumentUpload />} />
                <Route path="/start-magic" element={<URLInputPage />} />
                <Route path="/select-sw-option" element={<SelectSwOption />} />
                <Route path="/select-model" element={<SelectModel />} />
                <Route path="/access-knowledge-base" element={<AccessKnowledgeBase />} />
                <Route path="/knowledge-base/:surveyid" element={<PublicDash />} />
                <Route path="/pricing" element={<PricingPlans />} />
                <Route path="/persona-library" element={<PersonaLibrary />} />
                <Route path="/interview-library" element={<InterviewLibrary />} />
              </Route>

              <Route element={<FreeLayoutNoSidebar />}>
                <Route path="/start-magic-survey" element={<URLInputPageLoggedIn />} />
              </Route>

              <Route element={<LandingLayout />}>{/* <Route path="/landing" element={<LandingGEM />} /> */}</Route>
              <Route element={<FreeLayoutNoSidebarNoHeader />}>
                <Route path="/edit-magic-survey/:surveyid" element={<EditMagicSurvey />} />
                <Route path="/merged-survey-form" element={<MergedSurveyForm />} />
              </Route>

              <Route element={<NoLayout />}>
                <Route path="/about-us" element={<About />} />
                <Route path="/terms" element={<Terms />} />

                <Route path="/select-product" element={<SelectProduct />} />
                <Route path="/register" element={<HomeGuest />} />
                <Route path="/validate-email/:token" element={<VerifyEmail />} />
                <Route path="/request-reset-password/" element={<RequestResetPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/test-survey" element={<TestSurvey />} />
                {/* <Route path="/survey-playground" element={<SurveyPlayground />} />
                <Route path="/form-playground/:customer?" element={<FormPlayground />} />
                <Route path="/response-playground" element={<ResponsePlayground />} />
                <Route path="/table-playground" element={<TablePlayground />} />
                <Route path="/survey-analysis-card" element={<SurveyAnalysisCard />} />
                <Route path="/form-demo/:customer?" element={<FormDemo />} /> */}
                {/* <Route path="/start-magic" element={<URLInputPage />} /> */}
                {/* <Route path="/magic-survey" element={<DemoFormPage />} />

                <Route path="/magic-survey-form" element={<MagicFormPage />} />
                <Route path="/merged-survey-form" element={<MergedSurveyForm />} />
                <Route path="/start-magic-beta" element={<URLInputPageBeta />} />
                <Route path="/language-selector" element={<LanguageSelector />} /> */}

                {/* <Route path="/external-platforms" element={<ExternalPlatforms />} />
                <Route path="/tally-integration" element={<TallyIntegration />} />
                <Route path="/select-option" element={<SelectOption />} /> */}
              </Route>

              <Route element={<MetaLayout />}>
                <Route path="/sidenav" element={<MySideNav />} />
              </Route>

              <Route element={<ReportLayout />}>
                <Route path="/dashboard/:username/*" element={<Dashboard />} />
                <Route path="/model-settings/:surveyid" element={<ModelSettings />} />
                <Route path="/GEM/analysis1/:surveyid/:sectionid" element={<GEMAnalysis />} />
                <Route path="/SERVPERF/analysis/:surveyid/:sectionid" element={<SERVPERFAnalysis />} />
                <Route path="/GEM/analysis/:surveyid/:sectionid" element={<GEMAnalysis1 />} />
              </Route>

              <Route path="/start-survey/:id/:demoid?" element={<StartMixer />} />
              <Route path="/type1/:id/:sid/:demoid?" element={<FillSurveySingle />} />
              <Route path="/thank-you/:demoid?" element={<ThankYou />} />
              <Route path="/open-visuals/:surveyid/:accessid" element={<OpenVisuals />} />
              {/* <Route path="/new-minimal" element={<MinimalSurveyComponent />} /> */}
              {/* <Route path="/google-forms" element={<GoogleForms />} /> */}
              <Route path="/googled" element={<ThankYou />} />
              {/* <Route path="/new-dash" element={<NewDash />} />
              <Route path="/new-dashboard" element={<NewDashboard />} /> */}
              <Route path="/view-feedback-openai/:surveyid/:filter?/:timekey?" element={<ViewFeedbacksOpenAI />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="auth-callback" element={<AuthCallback />} />
              <Route path="/excel-upload" element={<ExcelUpload />} />
              <Route path="/gradio-embed" element={<GradioInterface />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CSSTransition timeout={330} in={state.isSearchOpen} classNames="search-overlay" unmountOnExit>
              <Search />
            </CSSTransition>
          </Suspense>
        </BrowserRouter>
      </DispatchContext.Provider>
    </StateContext.Provider>
  );
}

const root = ReactDOM.createRoot(document.querySelector("#app"));
root.render(<Main />);

if (module.hot) {
  module.hot.accept();
}
